import React, { useRef, useState, useEffect, useContext } from 'react';
import styled from '@benzinga/themetron';
// import { useTranslation } from 'react-i18next';
// import i18n from '@benzinga/translate';
import { StockInfoHeader } from '@benzinga/quotes-ui';
import { StockInfoChart } from '@benzinga/quotes-ui';
import { Icon } from '@benzinga/core-ui';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { ArticleFeaturedTicker } from '../entities';
import { useScrollShadow } from '@benzinga/hooks';
import { RankingDetail, QuotesManager } from '@benzinga/quotes-manager';
import { SessionContext } from '@benzinga/session-context';

const isCryptoTicker = (ticker: ArticleFeaturedTicker) => ticker.type === 'CRYPTO';

interface ArticleFeaturedTickersListProps {
  tickers: ArticleFeaturedTicker[];
}

export const ArticleFeaturedTickersList: React.FC<ArticleFeaturedTickersListProps> = ({ tickers }) => {
  // const { t } = useTranslation('quote', { i18n });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const session = useContext(SessionContext);
  const [rankingData, setRankingData] = useState<RankingDetail | null>(null);
  const [isLoadingRankingData, setIsLoadingRankingData] = useState(true);

  useEffect(() => {
    const fetchFreshRankingData = async () => {
      if (!tickers[0]?.symbol) {
        setIsLoadingRankingData(false);
        return;
      }

      try {
        const quotesManager = session.getManager(QuotesManager);
        const response = await quotesManager.getTickerDetails([tickers[0].symbol]);

        if (response?.ok?.result?.[0]) {
          setRankingData(response.ok.result[0].rankings ?? null);
        }
      } catch (error) {
        console.warn('Failed to fetch ranking data:', error);
        setRankingData(null);
      } finally {
        setIsLoadingRankingData(false);
      }
    };

    fetchFreshRankingData();
  }, [tickers, session]);

  const { showEnd } = useScrollShadow(scrollContainerRef, 'vertical');

  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({
        behavior: 'smooth',
        top: container.scrollHeight,
      });
    }
  };

  return (
    <Container className="article-header-ticker-list h-full">
      <div className="flex flex-col w-full h-full">
        <div
          className={`article-header-main-ticker ${isCryptoTicker(tickers[0]) ? 'min-h-[250px]' : 'min-h-[325px]'}`}
          key={tickers[0].symbol}
        >
          <div className="mb-1">
            <StockInfoChart
              changePercent={tickers[0].changePercent}
              isLoading={isLoadingRankingData}
              isPrimaryTicker
              logoUrl={tickers[0].logoUrl}
              name={tickers[0].name}
              price={tickers[0].price}
              rankingData={rankingData}
              symbol={tickers[0].symbol}
              type={tickers[0].type}
            />
          </div>
        </div>
        <div className="block text-center rounded-b-[8px] bg-[#223E6C] mb-3 py-2 uppercase ">
          <a className="block font-semibold text-white" href={`/quote/${tickers[0].symbol}`}>
            Overview
          </a>
        </div>
        {tickers.length > 1 && (
          <div className="relative w-full h-full">
            <div
              className="scrollable-container flex flex-col gap-3 w-full max-h-[220px] overflow-y-auto mt-auto"
              ref={scrollContainerRef}
            >
              {tickers.map((ticker, index) => {
                if (index === 0) {
                  return null;
                }
                return (
                  <StockInfoHeader
                    changePercent={ticker.changePercent}
                    key={ticker.symbol}
                    logoUrl={ticker.logoUrl}
                    name={ticker.name}
                    price={ticker.price}
                    symbol={ticker.symbol}
                    type={ticker.type}
                  />
                );
              })}
            </div>
            {showEnd && (
              <div className="fade-bottom">
                <Icon className="scroll-arrow" icon={faChevronDown} onClick={scrollToBottom} />
              </div>
            )}
          </div>
        )}
      </div>
    </Container>
  );
};

ArticleFeaturedTickersList.displayName = 'ArticleFeaturedTickersList';

const Container = styled.div`
  &.article-header-ticker-list {
    width: 100%;
    max-width: 450px;

    .scrollable-container {
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }

    .fade-bottom {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60px;
      background: linear-gradient(to bottom, transparent, #0e1d32 90%);
      pointer-events: none;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      padding: 0 1rem 0.5rem 0;

      .scroll-arrow {
        color: #5b7292;
        background: #192b45;
        border-radius: 50%;
        padding: 0.25rem;
        cursor: pointer;
        pointer-events: auto;
        transition: all 0.2s ease;

        &:hover {
          background: #21334d;
          color: white;
        }
      }
    }

    .article-header-main-ticker {
      background-color: #0e1d32;
      border: 1px solid #2c3d55;
      border-radius: 0.5rem;
      padding: 1rem;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      @media screen and (max-width: 500px) {
        padding: 0.8rem;
      }
    }

    .stock-info-header:not(.article-header-main-ticker *) {
      background-color: #0e1d32;
      border: 1px solid #2c3d55;
      border-radius: 0.5rem;
      padding: 0.8rem 1rem;
    }

    .stock-info-header {
      @media screen and (max-width: 500px) {
        font-size: 0.9rem;
        padding: unset;
      }
    }

    .overview-rating-title {
      color: ${({ theme }) => theme.colorPalette.gray400};
      font-size: ${({ theme }) => theme.fontSize.sm};
    }

    .overview-rating-value {
      font-size: ${({ theme }) => theme.fontSize.lg};
      text-transform: uppercase;
    }

    .technicals-financials-analysis {
      padding: 0;

      .info-tooltip-title {
        color: #5b7292;
        font-size: ${({ theme }) => theme.fontSize.base};
      }

      .range-indicator-value {
        color: ${({ theme }) => theme.colorPalette.white};
      }

      .ratings-section {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .divider-wrapper {
        display: none;
      }

      .range-indicator {
        width: 100%;
      }
    }

    .buttons-container {
      > div {
        width: 50%;
      }
    }

    .add-to-watchlist-button {
      flex-direction: row-reverse;
      justify-content: space-between;
      background-color: #192b45;
      border: none;
      line-height: unset;
      width: 100%;
      height: 2rem;

      &:hover {
        background-color: #21334d;
      }

      .watchlist-dropdown-icon {
        margin-right: 0;
      }

      svg {
        color: ${({ theme }) => theme.colorPalette.blue500};
      }
    }

    .overview-button {
      background-color: #192b45;
      border: none;
      color: ${({ theme }) => theme.colorPalette.white};
      justify-content: space-between;
      width: 50%;

      &:hover {
        background-color: #21334d;
      }
    }
  }
`;

export default ArticleFeaturedTickersList;
